import { Button } from '@dispatch-ui/react';
import {
  Control,
  Controller,
  FieldError,
  FieldValues,
  UseControllerProps,
} from 'react-hook-form';
import { LuX } from 'react-icons/lu';
import FormErrorLabel from '../form-error-label';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../select/select';
import LabelForm from './label-form';
import { getInputErrorClassName } from './styles.util';

export type SelectItemProps = {
  value: string;
  text: string;
};

function SelectInputForm<T extends FieldValues>({
  control,
  controllerProps,
  nonControlledProps,
  error,
  items,
  label,
  labelHelp,
  required = false,
  removableItems = false,
  placeholder = 'Select state',
}: {
  control?: Control<T>;
  controllerProps?: Omit<UseControllerProps<T>, 'data-testid'>;
  nonControlledProps?: {
    value?: string | null;
    onChange: (newType: string | null) => void;
    className?: string;
  };
  error?: FieldError;
  items: SelectItemProps[];
  label?: string;
  labelHelp?: string;
  required?: boolean;
  removableItems?: boolean;
  placeholder?: string;
}) {
  const inputClassName = getInputErrorClassName(!!error);

  if (nonControlledProps) {
    return (
      <div className={nonControlledProps.className}>
        <Select
          key={nonControlledProps.value || 'default'}
          value={nonControlledProps.value || undefined}
          onValueChange={newType => {
            nonControlledProps.onChange(newType);
          }}
        >
          <SelectTrigger data-testid="filter-select-event-type">
            <span className={!nonControlledProps.value ? 'text-gray-600' : ''}>
              <SelectValue placeholder={placeholder} />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {items.map(item => (
                <SelectItem key={item.value} value={item.value}>
                  {item.text}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
        {removableItems && nonControlledProps.value && (
          <Button
            data-testid="button-remove-event-type"
            variant="link"
            onClick={() => {
              nonControlledProps.onChange(null);
            }}
          >
            <LuX />
            <span className="sr-only">Remove item</span>
          </Button>
        )}
      </div>
    );
  }

  if (!control || !controllerProps) {
    return null;
  }

  return (
    <div>
      <div className="mb-1">
        <LabelForm
          label={label}
          labelHelp={labelHelp}
          required={required}
          hasError={!!error}
        />
      </div>
      <Controller
        control={control}
        {...controllerProps}
        render={({ field: { onChange, value } }) => (
          <div className="flex">
            <Select
              value={value}
              onValueChange={newValue => onChange(newValue)}
            >
              <SelectTrigger
                className={`w-full ${inputClassName}`}
                data-testid={`select-${controllerProps.name}`}
              >
                <span className="text-gray-600">
                  <SelectValue placeholder={placeholder} />
                </span>
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {items.map(item => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.text}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {removableItems && !!value && (
              <Button
                data-testid="button-remove-item"
                variant="link"
                onClick={() => {
                  onChange(undefined);
                }}
              >
                <LuX className="h-3 w-3" />
                <span className="sr-only">Remove item</span>
              </Button>
            )}
          </div>
        )}
      />
      {error && <FormErrorLabel>{error.message}</FormErrorLabel>}
    </div>
  );
}

export default SelectInputForm;
